import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DirectorFlat } from '../models/director';
import { DirectorSearchFilter } from '../models/directorSearchFilter';
import { PaginatedResponse } from '../models/paginatedResponse';
import { BulkMergeRequest } from '../models/bulkMergeRequest';
import { CreateDirectorRequest } from '../models/api/createDirectorRequest';
import { UpdateDirectorRequest } from '../models/api/updateDirectorRequest';
import { DirectorsSearchRequest } from '../models/api/directorsSearchRequest';
import { CompanyBoardMemberDto } from '../models/api/companyBoardMemberDto';
import { LargeShareholderPyramidDto } from '../models/api/largeShareholderPyramidDto';
import { DirectorPositionsResponseDto } from '../models/api/directorPositionsDto';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DirectorService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/directors`;
  private customerApiUrl = `${environment.apiUrl}/customer/directors`;

  /**
   * Search for directors with pagination and filtering
   */
  searchDirectors(request: DirectorsSearchRequest): Observable<PaginatedResponse<DirectorFlat>> {
    return this.http.post<PaginatedResponse<DirectorFlat>>(`${this.apiUrl}/search`, request);
  }

  /**
   * Customer-scoped search; requires the customer's CanAccessDirectors flag server-side.
   */
  customerSearchDirectors(request: DirectorsSearchRequest): Observable<PaginatedResponse<DirectorFlat>> {
    return this.http.post<PaginatedResponse<DirectorFlat>>(`${this.customerApiUrl}/search`, request);
  }

  /**
   * Get a director by ID
   */
  getDirectorById(id: string): Observable<DirectorFlat> {
    return this.http.get<DirectorFlat>(`${this.apiUrl}/${id}`);
  }

  /**
   * Customer-scoped get by ID.
   */
  customerGetDirectorById(id: string): Observable<DirectorFlat> {
    return this.http.get<DirectorFlat>(`${this.customerApiUrl}/${id}`);
  }

  /**
   * Customer-scoped positions endpoint.
   */
  customerGetDirectorPositions(id: string): Observable<DirectorPositionsResponseDto> {
    return this.http.get<DirectorPositionsResponseDto>(`${this.customerApiUrl}/${id}/positions`);
  }

  /**
   * Customer-scoped photo endpoint.
   */
  customerGetDirectorPhoto(id: string): Observable<Blob> {
    return this.http.get(`${this.customerApiUrl}/${id}/photo`, { responseType: 'blob' });
  }

  /**
   * Create a new director
   */
  createDirector(request: CreateDirectorRequest): Observable<DirectorFlat> {
    return this.http.post<DirectorFlat>(this.apiUrl, request);
  }

  /**
   * Update an existing director
   */
  updateDirector(id: string, request: UpdateDirectorRequest): Observable<DirectorFlat> {
    return this.http.put<DirectorFlat>(`${this.apiUrl}/${id}`, request);
  }

  /**
   * Delete a director
   */
  deleteDirector(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Export directors to CSV based on search criteria
   */
  exportDirectorsToCsv(request: DirectorsSearchRequest): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/search/export`, request, {
      responseType: 'blob'
    });
  }

  /**
   * Merge two directors (leftId is kept, rightId is merged into it)
   */
  mergeDirectors(leftId: string, rightId: string): Observable<void> {
    const params = new HttpParams()
      .set('leftId', leftId)
      .set('rightId', rightId);

    return this.http.post<void>(`${this.apiUrl}/merge`, null, { params });
  }

  /**
   * Bulk merge multiple directors into one
   */
  bulkMergeDirectors(request: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/merge/bulk`, request);
  }

  /**
   * Get references for a director
   */
  getDirectorReferences(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}/references`);
  }

  /**
   * Upload a photo for a director
   */
  uploadDirectorPhoto(id: string, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<void>(`${this.apiUrl}/${id}/photo`, formData);
  }

  /**
   * Get a director's photo
   */
  getDirectorPhoto(id: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${id}/photo`, {
      responseType: 'blob'
    });
  }

  /**
   * Delete a director's photo
   */
  deleteDirectorPhoto(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}/photo`);
  }

  /**
   * Get a director's board memberships
   */
  getDirectorBoardMemberships(id: string): Observable<CompanyBoardMemberDto[]> {
    return this.http.get<CompanyBoardMemberDto[]>(`${this.apiUrl}/${id}/board-memberships`);
  }

  /**
   * Get all board and executive positions for a director with detailed yearly info
   */
  getDirectorPositions(id: string): Observable<DirectorPositionsResponseDto> {
    return this.http.get<DirectorPositionsResponseDto>(`${this.apiUrl}/${id}/positions`);
  }

  /**
   * Get a director's large shareholders
   */
  getDirectorLargeShareholders(id: string): Observable<LargeShareholderPyramidDto[]> {
    return this.http.get<LargeShareholderPyramidDto[]>(`${this.apiUrl}/${id}/large-shareholders`);
  }

  /**
   * Legacy method for backwards compatibility - use searchDirectors instead
   * @deprecated Use searchDirectors for paginated results
   */
  getDirectors(searchFilter: DirectorSearchFilter): Observable<PaginatedResponse<DirectorFlat>> {
    return this.searchDirectors({
      ...searchFilter,
      pageNumber: 1,
      pageSize: 100,
      sortBy: 'FullName',
      ascending: true
    });
  }
}
